import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Close as CloseIcon, EventBusy as EventBusyIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import masavService from '../../../Services/masav.service';
import s from './MasavPaymentDayNotice.module.scss';

const COLLAPSED_CHIP_COUNT = 6;

const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '');

function pickPendingClient(raw) {
    return {
        caseId: raw.caseId ?? raw.CaseId ?? '',
        clientName: raw.clientName ?? raw.ClientName ?? '',
        paymentDayOfMonth: raw.paymentDayOfMonth ?? raw.PaymentDayOfMonth ?? null,
        dueDate: raw.dueDate ?? raw.DueDate ?? null,
    };
}

/**
 * MASAV clients whose payment day for the current month has not arrived yet.
 * Leading business → all of the accountant's clients; regular business → only `caseId`.
 */
export function useMasavPendingPaymentDays({ accountantId, caseId, isLeadingBusiness, reloadKey = 0, enabled = true }) {
    const [clients, setClients] = useState([]);
    const [today, setToday] = useState(null);

    useEffect(() => {
        if (!enabled || !accountantId || (!isLeadingBusiness && !caseId)) {
            setClients([]);
            return undefined;
        }
        let cancelled = false;
        masavService
            .getMasavPendingPaymentDay(accountantId, isLeadingBusiness ? null : caseId)
            .then((data) => {
                if (cancelled) return;
                setToday(data?.today ?? data?.Today ?? null);
                setClients((data?.clients ?? data?.Clients ?? []).map(pickPendingClient));
            })
            .catch((e) => {
                if (cancelled) return;
                console.error(e);
                setClients([]);
            });
        return () => {
            cancelled = true;
        };
    }, [enabled, accountantId, caseId, isLeadingBusiness, reloadKey]);

    return { clients, today };
}

/**
 * Warning banner (+ one toast per session) for clients excluded from MASAV files
 * until their payment day. Pass `pending` to reuse data already loaded by the parent.
 */
const MasavPaymentDayNotice = ({ accountantId, caseId, isLeadingBusiness, reloadKey = 0, pending, sx }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const own = useMasavPendingPaymentDays({
        accountantId,
        caseId,
        isLeadingBusiness,
        reloadKey,
        enabled: pending === undefined,
    });
    const { clients, today } = pending ?? own;
    const [expanded, setExpanded] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    const scopeKey = isLeadingBusiness ? 'all' : caseId;
    const first = clients[0];

    useEffect(() => {
        setDismissed(false);
        setExpanded(false);
    }, [accountantId, scopeKey]);

    useEffect(() => {
        if (!clients.length || !accountantId) return;
        const dayKey = today ? dayjs(today).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
        const storageKey = `masavPayDayNotice:${accountantId}:${scopeKey}:${dayKey}`;
        try {
            if (sessionStorage.getItem(storageKey)) return;
            sessionStorage.setItem(storageKey, '1');
        } catch {
            /* sessionStorage unavailable — still show the toast */
        }
        toast.warning(
            isLeadingBusiness
                ? t('MasavPayDayToastMany', { count: clients.length })
                : t('MasavPayDayToastOne', { date: formatDate(clients[0].dueDate) }),
            { toastId: storageKey }
        );
    }, [clients, accountantId, scopeKey, isLeadingBusiness, today, t]);

    if (!clients.length || dismissed) return null;

    const visibleClients = expanded ? clients : clients.slice(0, COLLAPSED_CHIP_COUNT);
    const hiddenCount = clients.length - visibleClients.length;

    return (
        <Box dir={direction} className={s.notice} sx={sx} role="status">
            <span className={s.iconWrap}>
                <EventBusyIcon fontSize="small" />
            </span>
            <div className={s.content}>
                {isLeadingBusiness ? (
                    <>
                        <p className={s.title}>{t('MasavPayDayBannerMany', { count: clients.length })}</p>
                        <div className={s.chips}>
                            {visibleClients.map((c) => (
                                <Tooltip key={c.caseId} title={c.caseId}>
                                    <span className={s.chip}>
                                        <span className={s.chipName}>{c.clientName || c.caseId}</span>
                                        <span className={s.chipDate}>{formatDate(c.dueDate)}</span>
                                    </span>
                                </Tooltip>
                            ))}
                            {clients.length > COLLAPSED_CHIP_COUNT && (
                                <button type="button" className={s.linkBtn} onClick={() => setExpanded((v) => !v)}>
                                    {expanded
                                        ? t('MasavPayDayHideList')
                                        : t('MasavPayDayShowMore', { count: hiddenCount })}
                                </button>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <p className={s.title}>{t('MasavPayDayBannerOneTitle', { date: formatDate(first?.dueDate) })}</p>
                        <p className={s.subtitle}>{t('MasavPayDayBannerOneHint')}</p>
                    </>
                )}
            </div>
            <Tooltip title={t('Close')}>
                <IconButton size="small" className={s.close} onClick={() => setDismissed(true)} aria-label={t('Close')}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

export default MasavPaymentDayNotice;
